import React, { useEffect, useState } from "react";
import { QRCodeSVG } from "qrcode.react";

const AppInstallModal = ({ isOpen, onClose }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const appStoreLink = "https://savy.onelink.me/o5ue/ekkh2fg2";

  // Detect mobile device
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  useEffect(() => {
    if (isOpen) {
      setIsVisible(true);
      setTimeout(() => setIsAnimating(true), 10);
    } else {
      setIsAnimating(false);
      setTimeout(() => setIsVisible(false), 300);
    }
  }, [isOpen]);

  if (!isOpen && !isVisible) return null;

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleButtonClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    // Add ripple effect
    const button = e.currentTarget;
    const ripple = document.createElement("span");
    const rect = button.getBoundingClientRect();
    const size = Math.max(rect.width, rect.height);
    const x = e.clientX - rect.left - size / 2;
    const y = e.clientY - rect.top - size / 2;

    ripple.style.width = ripple.style.height = `${size}px`;
    ripple.style.left = `${x}px`;
    ripple.style.top = `${y}px`;
    ripple.classList.add("ripple");

    button.appendChild(ripple);
    setTimeout(() => ripple.remove(), 600);

    // Open app store link
    window.open(appStoreLink, "_blank");
  };

  return (
    <>
      <div
        className={`fixed inset-0 z-50 flex items-start justify-center bg-black transition-all duration-500 ease-out overflow-y-auto pt-4 sm:pt-6 md:pt-8 ${
          isVisible ? "opacity-100" : "opacity-0"
        } ${isOpen ? "bg-opacity-30" : "bg-opacity-0"}`}
        style={{
          paddingTop: "calc(env(safe-area-inset-top) + 1rem)",
          paddingBottom: "calc(env(safe-area-inset-bottom) + 1rem)",
          paddingLeft: "env(safe-area-inset-left)",
          paddingRight: "env(safe-area-inset-right)",
        }}
        onClick={handleBackdropClick}
      >
        <div
          className={`bg-white rounded-3xl max-w-5xl w-full mx-4 mb-4 sm:mb-6 md:mb-8 px-4 sm:px-6 md:px-8 py-4 sm:py-5 md:py-6 text-center shadow-2xl relative transform transition-all duration-500 ease-out max-h-[calc(100vh-4rem)] sm:max-h-[calc(100vh-5rem)] md:max-h-[calc(100vh-6rem)] flex flex-col overflow-hidden ${
            isAnimating && isOpen
              ? "scale-100 opacity-100 translate-y-0"
              : "scale-90 opacity-0 translate-y-8"
          }`}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Animated background gradient */}
          <div className="absolute inset-0 rounded-3xl overflow-hidden pointer-events-none">
            <div className="absolute -top-20 -right-20 w-64 h-64 bg-gradient-to-br from-[#8AB9F1] via-[#B57EDC] to-[#F08080] rounded-full opacity-10 blur-3xl animate-pulse"></div>
            <div
              className="absolute -bottom-20 -left-20 w-64 h-64 bg-gradient-to-tr from-[#F08080] via-[#B57EDC] to-[#8AB9F1] rounded-full opacity-10 blur-3xl animate-pulse"
              style={{ animationDelay: "1s" }}
            ></div>
          </div>

          {/* Close Button - Top Right */}
          <button
            onClick={onClose}
            className={`absolute top-4 right-4 sm:top-6 sm:right-6 z-20 text-gray-400 hover:text-gray-700 transition-colors ${
              isAnimating ? "opacity-100" : "opacity-0"
            }`}
            style={{ transitionDelay: isAnimating ? "100ms" : "0ms" }}
          >
            <svg
              className="w-6 h-6 sm:w-7 sm:h-7"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>

          {/* Header */}
          <div className="relative z-10 mb-4 sm:mb-5">
            <div
              className={`mb-4 transition-all duration-700 delay-100 ease-out ${
                isAnimating
                  ? "opacity-100 translate-y-0"
                  : "opacity-0 translate-y-4"
              }`}
            >
              <div className="inline-flex items-center justify-center w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-gradient-to-br from-[#8AB9F1] via-[#B57EDC] to-[#F08080] mb-4 shadow-lg">
                <svg
                  className="w-8 h-8 sm:w-10 sm:h-10 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z"
                  />
                </svg>
              </div>
            </div>
            <h2
              className={`text-xl sm:text-2xl md:text-3xl font-bold text-[#141A33] mb-2 sm:mb-3 transition-all duration-700 delay-200 ease-out ${
                isAnimating
                  ? "opacity-100 translate-y-0"
                  : "opacity-0 translate-y-4"
              }`}
            >
              Continue to Get Your Refund
            </h2>
            <p
              className={`text-sm sm:text-base md:text-lg text-gray-600 mb-1 sm:mb-2 transition-all duration-700 delay-300 ease-out ${
                isAnimating
                  ? "opacity-100 translate-y-0"
                  : "opacity-0 translate-y-4"
              }`}
            >
              Install the{" "}
              <span className="font-semibold text-[#002147]">Savy</span> mobile
              app from the App Store
            </p>
            <p
              className={`text-xs sm:text-sm text-gray-500 transition-all duration-700 delay-300 ease-out ${
                isAnimating ? "opacity-100" : "opacity-0"
              }`}
            >
              to complete your refund process
            </p>
          </div>

          {/* Mobile Layout: Steps + Download Button + About Savy (Scrollable) */}
          {isMobile ? (
            <div className="relative z-10 flex flex-col flex-1 min-h-0 overflow-hidden">
              {/* Steps */}
              <div
                className={`flex flex-col space-y-2 text-sm text-gray-700 bg-gray-50 rounded-xl p-4 mb-4 transition-all duration-700 delay-500 ease-out ${
                  isAnimating ? "opacity-100" : "opacity-0"
                }`}
              >
                <div className="flex items-center space-x-3 w-full">
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-[#8AB9F1] flex items-center justify-center text-white font-bold">
                    1
                  </div>
                  <span className="flex-1 text-left">
                    Download the Savy app from the App Store
                  </span>
                </div>
                <div className="flex items-center space-x-3 w-full">
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-[#b57edc] flex items-center justify-center text-white font-bold">
                    2
                  </div>
                  <span className="flex-1 text-left">
                    Complete your refund process in the app
                  </span>
                </div>
              </div>

              {/* Download Button */}
              <button
                onClick={handleButtonClick}
                className={`w-full bg-[#002147] text-white py-3.5 rounded-xl font-semibold hover:bg-[#003366] transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-[1.02] active:scale-[0.98] relative overflow-hidden z-10 mb-4 flex items-center justify-center ${
                  isAnimating
                    ? "opacity-100 translate-y-0"
                    : "opacity-0 translate-y-4"
                }`}
                style={{ transitionDelay: isAnimating ? "600ms" : "0ms" }}
              >
                <span className="relative z-10 flex items-center justify-center space-x-3">
                  <svg
                    className="w-5 h-5"
                    fill="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.4C1.79 15.25 2.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
                  </svg>
                  <span className="text-base">Download from App Store</span>
                </span>
              </button>

              {/* About Savy Text - Scrollable on Mobile */}
              <div
                className={`w-full bg-gray-50 rounded-xl p-4 transition-all duration-700 ease-out flex-1 min-h-0 flex flex-col overflow-hidden ${
                  isAnimating
                    ? "opacity-100 translate-y-0"
                    : "opacity-0 translate-y-4"
                }`}
                style={{
                  transitionDelay: isAnimating ? "700ms" : "0ms",
                  opacity: isAnimating ? 0.85 : 0,
                }}
              >
                <h3 className="text-xs font-semibold text-[#002147] mb-2.5 opacity-90 flex-shrink-0">
                  About Savy
                </h3>
                <div className="text-[10px] text-gray-600 space-y-2 leading-relaxed opacity-80 overflow-y-auto flex-1">
                  <p>
                    Savy is a tax claims management company registered with HMRC
                    (Agent code 2916DZ).
                  </p>
                  <p>
                    We work on a no win no fee basis and charge 25% of any
                    successful claim - so you keep 75%. You don't have to use a
                    claims management company to make your claim. You can claim
                    directly yourself for free to HMRC.
                  </p>
                  <p className="font-medium text-[#002147] opacity-90">
                    The average work traveller that claims a tax refund from
                    Savy gets a{" "}
                    <span className="font-semibold text-[#F08080]">£2,883</span>{" "}
                    tax refund.
                  </p>
                </div>
              </div>
            </div>
          ) : (
            /* Desktop Layout: Two Column - QR Code Left, Steps Right */
            <div className="relative z-10 flex flex-col md:flex-row gap-4 md:gap-6 items-center md:items-start mb-4 sm:mb-5 flex-1 min-h-0">
              {/* QR Code Container - Left */}
              <div
                className={`flex-shrink-0 transition-all duration-700 delay-400 ease-out ${
                  isAnimating ? "opacity-100 scale-100" : "opacity-0 scale-95"
                }`}
              >
                <div className="flex flex-col items-center">
                  <div className="relative p-4 sm:p-6 md:p-8 bg-gradient-to-br from-gray-50 via-white to-gray-50 rounded-3xl shadow-2xl border-2 border-gray-100 inline-block">
                    {/* Decorative corners */}
                    <div className="absolute top-0 left-0 w-6 h-6 sm:w-8 sm:h-8 border-t-4 border-l-4 border-[#8AB9F1] rounded-tl-3xl"></div>
                    <div className="absolute top-0 right-0 w-6 h-6 sm:w-8 sm:h-8 border-t-4 border-r-4 border-[#F08080] rounded-tr-3xl"></div>
                    <div className="absolute bottom-0 left-0 w-6 h-6 sm:w-8 sm:h-8 border-b-4 border-l-4 border-[#b57edc] rounded-bl-3xl"></div>
                    <div className="absolute bottom-0 right-0 w-6 h-6 sm:w-8 sm:h-8 border-b-4 border-r-4 border-[#8AB9F1] rounded-br-3xl"></div>

                    <div className="bg-white p-4 sm:p-5 md:p-6 rounded-2xl shadow-inner border border-gray-200">
                      <QRCodeSVG
                        value={appStoreLink}
                        size={180}
                        level="H"
                        includeMargin={true}
                        fgColor="#002147"
                        bgColor="#ffffff"
                      />
                    </div>
                  </div>
                  <p className="mt-2 sm:mt-3 md:mt-4 text-xs sm:text-sm text-gray-500 font-medium">
                    Scan with your phone camera
                  </p>
                </div>
              </div>

              {/* Instructions - Right */}
              <div
                className={`flex-1 w-full md:w-auto flex flex-col transition-all duration-700 delay-500 ease-out ${
                  isAnimating ? "opacity-100" : "opacity-0"
                }`}
              >
                <div className="flex flex-col space-y-2 sm:space-y-3 text-xs sm:text-sm md:text-base text-gray-700 bg-gray-50 rounded-xl p-3 sm:p-4 md:p-5 mb-4">
                  <div className="flex items-center space-x-3 w-full">
                    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-[#8AB9F1] flex items-center justify-center text-white font-bold">
                      1
                    </div>
                    <span className="flex-1 text-left">
                      Scan the QR code with your phone
                    </span>
                  </div>
                  <div className="flex items-center space-x-3 w-full">
                    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-[#b57edc] flex items-center justify-center text-white font-bold">
                      2
                    </div>
                    <span className="flex-1 text-left">
                      Download the Savy app from the App Store
                    </span>
                  </div>
                  <div className="flex items-center space-x-3 w-full">
                    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-[#F08080] flex items-center justify-center text-white font-bold">
                      3
                    </div>
                    <span className="flex-1 text-left">
                      Complete your refund process in the app
                    </span>
                  </div>
                </div>

                {/* About Savy Text - After Steps Vertically */}
                <div
                  className={`w-full bg-gray-50 rounded-xl p-3 sm:p-4 md:p-5 transition-all duration-700 ease-out ${
                    isAnimating
                      ? "opacity-100 translate-y-0"
                      : "opacity-0 translate-y-4"
                  }`}
                  style={{
                    transitionDelay: isAnimating ? "600ms" : "0ms",
                    opacity: isAnimating ? 0.85 : 0,
                  }}
                >
                  <h3 className="text-xs sm:text-sm font-semibold text-[#002147] mb-2.5 opacity-90">
                    About Savy
                  </h3>
                  <div className="text-[10px] sm:text-xs text-gray-600 space-y-2 leading-relaxed opacity-80">
                    <p>
                      Savy is a tax claims management company registered with
                      HMRC (Agent code 2916DZ).
                    </p>
                    <p>
                      We work on a no win no fee basis and charge 25% of any
                      successful claim - so you keep 75%. You don't have to use
                      a claims management company to make your claim. You can
                      claim directly yourself for free to HMRC.
                    </p>
                    <p className="font-medium text-[#002147] opacity-90">
                      The average work traveller that claims a tax refund from
                      Savy gets a{" "}
                      <span className="font-semibold text-[#F08080]">
                        £2,883
                      </span>{" "}
                      tax refund.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default AppInstallModal;
